import React, { useState, useMemo } from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBBadge,
  MDBInput,
} from "mdb-react-ui-kit";
import "./DualPicklist.css";

/**
 * DualPicklist — Reusable dual-list transfer component.
 *
 * Props:
 *   available       {Array<{label, value, ...}>}  Items on the left (available)
 *   selected        {Array<{label, value, ...}>}  Items on the right (selected)
 *   onAdd           {function(item)}              Called when item moves left→right
 *   onRemove        {function(item)}              Called when item moves right→left
 *   titleLeft       {string}                      Left column header (default "Disponibles")
 *   titleRight      {string}                      Right column header (default "Asignados")
 *   height          {string}                      Max height for scroll areas (default "260px")
 *   searchable      {boolean}                     Show search inputs (default true)
 *   renderItem      {function(item)}              Custom render for each item label
 *   isItemLocked    {function(item) => boolean}   If true, item cannot be removed from right
 *   renderLockInfo  {function(item) => ReactNode} Extra UI for locked items (e.g. badge)
 *   itemKey         {function(item) => string}    Key extractor (default item.value?.IDPermiso || item.label)
 *   disabled        {boolean}                     Disable all interactions
 */
export function DualPicklist({
  available = [],
  selected = [],
  onAdd,
  onRemove,
  titleLeft = "Disponibles",
  titleRight = "Asignados",
  height = "260px",
  searchable = true,
  renderItem,
  isItemLocked,
  renderLockInfo,
  itemKey,
  disabled = false,
}) {
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [filterLeft, setFilterLeft] = useState("");
  const [filterRight, setFilterRight] = useState("");

  const getKey = (item, index) => {
    if (itemKey) return itemKey(item);
    if (item.value?.IDPermiso != null) return item.value.IDPermiso;
    if (item.value?.IDGrupo != null) return item.value.IDGrupo;
    if (item.value?.IDAplicacion != null) return item.value.IDAplicacion;
    if (item.value?.IDUsuario != null) return item.value.IDUsuario;
    return index;
  };

  const filteredAvailable = useMemo(() => {
    if (!filterLeft.trim()) return available;
    const f = filterLeft.toLowerCase();
    return available.filter((item) => item.label.toLowerCase().includes(f));
  }, [available, filterLeft]);

  const filteredSelected = useMemo(() => {
    if (!filterRight.trim()) return selected;
    const f = filterRight.toLowerCase();
    return selected.filter((item) => item.label.toLowerCase().includes(f));
  }, [selected, filterRight]);

  const handleAdd = (item) => {
    if (disabled) return;
    setSelectedLeft(null);
    setSelectedRight(null);
    onAdd && onAdd(item);
  };

  const handleRemove = (item) => {
    if (disabled || (isItemLocked && isItemLocked(item))) return;
    setSelectedLeft(null);
    setSelectedRight(null);
    onRemove && onRemove(item);
  };

  const leftItem = selectedLeft != null ? filteredAvailable[selectedLeft] : null;
  const rightItem = selectedRight != null ? filteredSelected[selectedRight] : null;
  const isRightLocked = rightItem && isItemLocked && isItemLocked(rightItem);

  return (
    <div className="dual-picklist">
      <div className="dual-picklist-panel">
        <div className="dual-picklist-header">{titleLeft}</div>
        {searchable && (
          <div className="dual-picklist-search">
            <MDBInput
              type="text"
              size="sm"
              label="Buscar..."
              value={filterLeft}
              onChange={(e) => { setFilterLeft(e.target.value); setSelectedLeft(null); }}
              disabled={disabled}
            />
          </div>
        )}
        <div className="dual-picklist-list" style={{ maxHeight: height }}>
          {filteredAvailable.map((item, index) => (
            <div
              key={getKey(item, index)}
              className={`dual-picklist-item${selectedLeft === index ? " dual-picklist-item-active" : ""}`}
              onClick={() => !disabled && setSelectedLeft(index)}
              onDoubleClick={() => handleAdd(item)}
            >
              <span className="dual-picklist-item-label">
                {renderItem ? renderItem(item) : item.label}
              </span>
            </div>
          ))}
          {filteredAvailable.length === 0 && (
            <div className="dual-picklist-empty">
              {available.length === 0 ? "Sin elementos" : "Sin resultados"}
            </div>
          )}
        </div>
        <div className="dual-picklist-count">{available.length}</div>
      </div>

      <div className="dual-picklist-actions">
        <MDBBtn
          size="sm"
          className="dual-picklist-btn"
          disabled={disabled || selectedLeft === null}
          onClick={() => leftItem && handleAdd(leftItem)}
          title="Añadir"
        >
          <MDBIcon fas icon="chevron-right" />
        </MDBBtn>
        <MDBBtn
          size="sm"
          className="dual-picklist-btn"
          disabled={disabled || selectedRight === null || isRightLocked}
          onClick={() => rightItem && handleRemove(rightItem)}
          title="Quitar"
        >
          <MDBIcon fas icon="chevron-left" />
        </MDBBtn>
      </div>

      <div className="dual-picklist-panel">
        <div className="dual-picklist-header">{titleRight}</div>
        {searchable && (
          <div className="dual-picklist-search">
            <MDBInput
              type="text"
              size="sm"
              label="Buscar..."
              value={filterRight}
              onChange={(e) => { setFilterRight(e.target.value); setSelectedRight(null); }}
              disabled={disabled}
            />
          </div>
        )}
        <div className="dual-picklist-list" style={{ maxHeight: height }}>
          {filteredSelected.map((item, index) => {
            const locked = isItemLocked && isItemLocked(item);
            return (
              <div
                key={getKey(item, index)}
                className={`dual-picklist-item${selectedRight === index ? " dual-picklist-item-active" : ""}${locked ? " dual-picklist-item-locked" : ""}`}
                onClick={() => !disabled && setSelectedRight(index)}
                onDoubleClick={() => !locked && handleRemove(item)}
                title={locked && renderLockInfo ? "" : undefined}
              >
                <span className="dual-picklist-item-label">
                  {renderItem ? renderItem(item) : item.label}
                </span>
                {locked && renderLockInfo && (
                  <span className="dual-picklist-lock-info">
                    {renderLockInfo(item)}
                  </span>
                )}
              </div>
            );
          })}
          {filteredSelected.length === 0 && (
            <div className="dual-picklist-empty">
              {selected.length === 0 ? "Sin elementos" : "Sin resultados"}
            </div>
          )}
        </div>
        <div className="dual-picklist-count">{selected.length}</div>
      </div>
    </div>
  );
}
